import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import Avatar from '../../../components/Avatar';
import Tooltip from '../../../components/Tooltip';
import LogoIcon from '../../../assets/images/logo-50x50.svg';
import { __, dateFrom, timeFrom } from '../../../core/utils';
import { setNotification } from '../../../redux/actions/general/notification';
import {
  handleLeaveApproval,
  readLeaveApprovalStatus,
} from '../../../redux/actions/table/timesheets';
import styles from '../../../styles/flyouts/notifications/NotificationsFlyout.scss';

const getStatusUrl = (data) => {
  if (data?.url_status) {
    return data.url_status;
  }

  if (data?.leave_uuid) {
    return `/user_leaves/${data.leave_uuid}/approval_status`;
  }

  const uuid = data?.url_approve?.match(/approve_leave\/([^/?]+)/)?.[1];

  return uuid ? `/user_leaves/${uuid}/approval_status` : null;
};

const LeaveApprovalRequest = ({ notificationData }) => {
  const { data } = notificationData;
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const statusUrl = getStatusUrl(data);

  const loadStatus = () => {
    if (!statusUrl) {
      setLoadFailed(true);
      return Promise.resolve();
    }

    return readLeaveApprovalStatus(statusUrl)
      .then(({ response }) => {
        setApprovalStatus(response.data);
        setLoadFailed(false);
      })
      .catch(() => setLoadFailed(true));
  };

  useEffect(() => {
    setApprovalStatus(null);
    setLoadFailed(false);
    loadStatus();
  }, [statusUrl]);

  const submitApproval = (type) => {
    if (submitting) {
      return;
    }

    const endpoint = type === 'approve' ? data.url_approve : data.url_reject;

    setSubmitting(true);

    handleLeaveApproval(endpoint, type)
      .then(() => loadStatus())
      .then(() => {
        setNotification(
          __(
            `notification-flyout.leave_approval_request.${
              type === 'approve' ? 'approved' : 'rejected'
            }`,
          ),
        );
      })
      .catch(({ response }) => {
        setNotification({
          text:
            response?.error ||
            __('notification-flyout.leave_approval_request.action_failed'),
          type: 'warning',
        });
      })
      .finally(() => setSubmitting(false));
  };

  const isRange = data.message_type === 'range';
  const message = isRange
    ? __('notification.notification-content.range.leave_approver_request', {
        USER: data.user,
        START_TIME: moment(data.start_time).format('DD.MM.YYYY'),
        END_TIME: moment(data.end_time).format('DD.MM.YYYY'),
        LEAVE_REASON: data.leave_reason,
      })
    : __('notification.notification-content.date.leave_approver_request', {
        USER: data.user,
        DATE: moment(data.date).format('DD.MM.YYYY'),
        TIME: data.time,
        LEAVE_REASON: data.leave_reason,
      });

  return (
    <>
      <div
        className={classNames(styles.row, notificationData.read && styles.read)}
      >
        <div className={classNames(styles.activityAndContent, styles.column)}>
          {__('notification-flyout.activity-name.' + notificationData.action)}
          <br />
          {message}

          {loadFailed && (
            <div className={styles.leaveApprovalError}>
              {__(
                'notification-flyout.leave_approval_request.status_unavailable',
              )}
            </div>
          )}
        </div>

        <div className={styles.triggered_by}>
          {notificationData.triggered_by ? (
            <Avatar
              avatar_url={notificationData.triggered_by.avatar_url}
              name={
                notificationData.triggered_by.fullname +
                '\n' +
                notificationData.triggered_by.company_name
              }
              active={notificationData.triggered_by.active}
              tooltip={true}
            />
          ) : (
            <Tooltip text='System'>
              <LogoIcon className={styles.logo} />
            </Tooltip>
          )}
        </div>

        <div className={styles.date_time}>
          {dateFrom(notificationData.created_at)}
          <br />
          {timeFrom(notificationData.created_at)}
        </div>
      </div>

      {approvalStatus && (
        <div
          className={classNames(
            styles.row,
            notificationData.read && styles.read,
            styles.flex,
          )}
        >
          <div className={styles.leaveApprovalTable}>
            {approvalStatus.approvers.map((approver) => (
              <div className={styles.leaveApprovalRow} key={approver.id}>
                <span className={styles.leaveApproverName}>
                  {approver.fullname}
                </span>
                <span
                  className={classNames(
                    styles.leaveApprovalStatus,
                    styles[approver.status],
                  )}
                >
                  {approver.status}
                </span>
                <Avatar
                  avatar_url={approver.avatar_url}
                  name={approver.fullname}
                  tooltip={false}
                />
                <span className={styles.leaveApprovalDate}>
                  {approver.responded_at ? (
                    <>
                      {dateFrom(approver.responded_at)}
                      <br />
                      {timeFrom(approver.responded_at)}
                    </>
                  ) : (
                    '—'
                  )}
                </span>
              </div>
            ))}

            {approvalStatus?.can_respond && (
              <div className={styles.leaveApprovalActions}>
                <button
                  disabled={submitting}
                  onClick={() => submitApproval('approve')}
                >
                  {__('approve')}
                </button>
                <button
                  disabled={submitting}
                  onClick={() => submitApproval('reject')}
                >
                  {__('reject')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default LeaveApprovalRequest;
